import { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';

import data from '../../data/funnelByChain.json';
import { useIsDark } from '../../useIsDark';
import { BREAKPOINTS, queryDown } from '@/lib/breakpoints';

/**
 * blog30's figure — the fundraising funnel, step by step, with what Raisy adds shown OUTSIDE it.
 *
 * THE STEPS (the PO's own list, and the order is the point):
 *   1 potential investor list   2 potential investor geography   3 investor fit >80%
 *   4 reachouts sent            5 response received              6 meetings booked
 *   7 further DD                8 investment
 *
 * CHART TYPE FIRST (RULES_BLOG_POST): the quantified part of that list is a FLOW — units entering
 * a process, some arriving at the next step and most falling out — so the chart-type table's
 * Sankey row applies. Rule 51 supplies the grammar: thickness is ONE named unit, every stage
 * conserves, the losses are drawn and labelled, and every rate carries its provenance.
 *
 * ⚠️ WHAT THE SANKEY DELIBERATELY DOES NOT DO. Four of the eight steps are GATES whose magnitude
 * has never been measured — the list, the geography filter, the fit >80% filter, and further DD.
 * They are drawn as gates, never as numbers: a filter rate nobody has measured cannot honestly
 * set the width of a band. Steps 1-3 are shown as the pre-filter that FEEDS the flow, and step 7
 * as a gate on the meetings-to-investment band (the 35% win rate spans it, so its own share is
 * not separated out).
 *
 * ⚠️ AND WHY THE VALUE PANEL SITS OUTSIDE THE CHART. The sankey shows what it takes at a given
 * reply band; it cannot show the difference a fit-ranked list makes, because a Sankey draws one
 * run. So the comparison — with Raisy against without — is a separate panel above it, on the two
 * reply bands blog16 publishes, which is the whole of the claim: the pre-filter moves the reply
 * band from 2-5% to 3-10%, and everything downstream follows.
 *
 * Data: `../../data/funnelByChain.json`. No number is typed into this file (Rule 27).
 * Theme: `dark` prop override, otherwise the live html class theme (Rule 29).
 */

type Step = { n: number; key: string; label: string; short: string; kind: 'gate' | 'flow'; measured: boolean; note?: string };
type Base = {
  key: string; label: string; is_raisy: boolean;
  reply_low: number; reply_high: number; reply_mid: number;
  meeting_rate: number; win_rate: number; source: string; note: string;
};
type Lane = { key: string; label: string; reachouts: number; source: string };
type Loss = { label: string; short: string };

const STEPS = data.steps as Step[];
const BASES = data.bases as Base[];
const LANES = data.lanes as Lane[];
const LOSSES = data.losses as Record<'no_response' | 'no_meeting' | 'no_investment', Loss>;

/**
 * Narrow viewport = below Tailwind's `sm`, where the four-column Sankey has ~90px per column and
 * the full step names collide (measured at 420px). Deliberately NOT `useIsMobile`, which reads
 * SHELL_SWITCH_PX (`md`) to decide the CRM shell's identity — this is chart geometry, a narrower
 * question, and the threshold comes from the shared token either way.
 */
const useIsNarrow = (): boolean => {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < BREAKPOINTS.sm : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(queryDown(BREAKPOINTS.sm));
    const onChange = () => setNarrow(window.innerWidth < BREAKPOINTS.sm);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return narrow;
};

const PALETTE = {
  raisy: '#0AB3BF',
  without: '#94a3b8',
  start: '#1d4ed8',
  loss: '#b91c1c',
  pass: '#0f766e',
  win: '#0ea5e9',
  gate: '#f59e0b',
};

/** ONE formatter for every number the figure shows — label, tooltip and axis alike (Rule 4). */
const fmt = (v: number): string =>
  v >= 1000 ? v.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : v >= 100 ? v.toFixed(0)
      : v >= 10 ? v.toFixed(1)
        : v.toFixed(1);

const pct = (v: number): string => `${(v * 100).toFixed(v < 0.01 ? 2 : 1)}%`;

/** Lighten a hex colour towards white — used for a band's floor and top tones. */
const tint = (hex: string, amount: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

export const FunnelSankeyChart = ({ height = 460, dark }: { height?: number; dark?: boolean }) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const narrow = useIsNarrow();
  const [laneKey, setLaneKey] = useState<string>('fundraising');
  const lane = useMemo(() => LANES.find((l) => l.key === laneKey) ?? LANES[0], [laneKey]);

  const raisy = useMemo(() => BASES.find((b) => b.is_raisy) ?? BASES[0], []);
  const without = useMemo(() => BASES.find((b) => !b.is_raisy) ?? BASES[1], []);

  /** The three outcomes a base produces at a volume, for a given reply rate. */
  const outcomes = (base: Base, replyRate: number) => {
    const R = lane.reachouts;
    const responses = R * replyRate;
    const meetings = responses * base.meeting_rate;
    const raised = meetings * base.win_rate;
    return { R, responses, meetings, raised };
  };

  const rMid = outcomes(raisy, raisy.reply_mid);
  const wMid = outcomes(without, without.reply_mid);
  const uplift = wMid.responses > 0 ? rMid.responses / wMid.responses : 0;

  // ⚠️ THE BANDS OVERLAP, AND THE HEADLINE MUST NOT HIDE IT. Raisy's floor (3%) sits BELOW the
  // typical list's ceiling (5%), so the honest claim is a RANGE, not a factor: 1.9x is the midpoint
  // comparison, 0.6x is the conservative ends against each other, 5.0x the optimistic ones. Stating
  // only the midpoint would be the overstatement this figure exists to avoid.
  const rLow = outcomes(raisy, raisy.reply_low);
  const rHigh = outcomes(raisy, raisy.reply_high);
  const wLow = outcomes(without, without.reply_low);
  const wHigh = outcomes(without, without.reply_high);
  const worstRatio = wHigh.responses > 0 ? rLow.responses / wHigh.responses : 0;
  const bestRatio = wLow.responses > 0 ? rHigh.responses / wLow.responses : 0;

  // ---- the Sankey: quantified steps only, gates named in the rail above ----
  const built = useMemo(() => {
    const { R, responses, meetings, raised } = outcomes(raisy, raisy.reply_mid);
    const noResponse = R - responses;
    const noMeeting = responses - meetings;
    const noRaise = meetings - raised;
    const S = STEPS;
    const name = (n: number) => {
      const s = S.find((x) => x.n === n)!;
      return narrow ? s.short : `${s.n}. ${s.label}`;
    };
    const loss = (k: keyof typeof LOSSES) => (narrow ? LOSSES[k].short : LOSSES[k].label);
    return {
      R, responses, meetings, raised,
      nodes: [
        { name: name(4), depth: 0, color: PALETTE.start, value: R },
        { name: loss('no_response'), depth: 1, color: PALETTE.loss, value: noResponse },
        { name: name(5), depth: 1, color: PALETTE.raisy, value: responses },
        { name: loss('no_meeting'), depth: 2, color: PALETTE.loss, value: noMeeting },
        { name: name(6), depth: 2, color: PALETTE.pass, value: meetings },
        { name: name(7), depth: 3, color: PALETTE.gate, value: meetings },
        { name: loss('no_investment'), depth: 4, color: PALETTE.loss, value: noRaise },
        { name: name(8), depth: 4, color: PALETTE.win, value: raised },
      ],
      links: [
        { source: name(4), target: loss('no_response'), value: noResponse },
        { source: name(4), target: name(5), value: responses },
        { source: name(5), target: loss('no_meeting'), value: noMeeting },
        { source: name(5), target: name(6), value: meetings },
        { source: name(6), target: name(7), value: meetings },
        { source: name(7), target: loss('no_investment'), value: noRaise },
        { source: name(7), target: name(8), value: raised },
      ],
    };
  }, [raisy, lane, narrow]);

  const text = isDark ? '#e5e7eb' : '#111827';
  const sub = isDark ? '#9ca3af' : '#6b7280';
  const axis = isDark ? '#374151' : '#e5e7eb';

  /**
   * Rule 956 (an opaque background is mandatory, and a transparent one is a publish blocker).
   * The figure paints its OWN surface rather than inheriting one, so it is opaque wherever it is
   * mounted — including the click-to-enlarge lightbox — and its label colours always sit on the
   * surface they were chosen for. A transparent chart is how dark-theme labels end up unreadable on
   * a light panel. `#0f172a` is the site's own `dark:bg-slate-900`.
   */
  const surface = isDark ? '#0f172a' : '#ffffff';

  // ---- panel A: the value, at the same volume, on the two published bands ----
  const metrics: { key: 'responses' | 'meetings' | 'raised'; label: string }[] = [
    { key: 'responses', label: 'Responses' },
    { key: 'meetings', label: 'Meetings' },
    { key: 'raised', label: 'Investments' },
  ];
  const bandOf = (base: Base, rate: number) => outcomes(base, rate);

  const valueOption = {
    backgroundColor: surface,
    grid: { left: 8, right: 16, top: narrow ? 46 : 28, bottom: 4, containLabel: true },
    tooltip: {
      trigger: 'item',
      backgroundColor: isDark ? '#111827' : '#fff',
      borderColor: axis,
      textStyle: { color: text, fontSize: 12 },
      formatter: (p: any) => {
        const x = p.data;
        if (!x || typeof x !== 'object' || !('low' in x)) return '';
        return `<b>${x.base}</b> · ${x.metric}<br/>published band <b>${fmt(x.low)}–${fmt(x.high)}</b> · midpoint <b>${fmt(x.mid)}</b>`;
      },
    },
    legend: { top: 0, data: [without.label, raisy.label], type: narrow ? 'scroll' : 'plain', textStyle: { color: text, fontSize: 11.5 }, icon: 'roundRect', itemWidth: 12, itemHeight: 8 },
    xAxis: { type: 'category', data: metrics.map((m) => m.label), axisLabel: { color: text, fontSize: 11.5 }, axisLine: { lineStyle: { color: axis } } },
    yAxis: { type: 'value', axisLabel: { color: sub, fontSize: 10.5, formatter: (v: number) => fmt(v) }, splitLine: { lineStyle: { color: axis, opacity: 0.5 } } },
    series: seriesStack(),
  };
  function seriesStack(): any[] {
    // A BAR IS ANCHORED AT ZERO, AND ITS TONES RUN IN ONE DIRECTION.
    // The first version carried the published band as three tones — palest 0→low, SOLID low→mid,
    // tint mid→high — which put the darkest band in the MIDDLE of the bar with a lighter band above
    // it. Nothing about that reads as anything: a reader sees a dark stripe in the middle and has no
    // reason to guess it is the midpoint. The PO's words were "i have never seen anything like it".
    //
    // Two tones, monotonic bottom-to-top, and each one says something a reader already expects:
    //   SOLID  = 0 → band LOW     "even at the bottom of the published range, at least this much"
    //   LIGHT  = band LOW → HIGH  "and here is how much higher the range reaches"
    // The full band is still on the figure (so the overlap between the two entities is visible), the
    // bar still starts at 0, and the midpoint travels on the datum for the tooltip.
    // Both segments share ONE series name so the legend toggles them together.
    const out: any[] = [];
    const pairs: [string, Base, string][] = [['without', without, PALETTE.without], ['raisy', raisy, PALETTE.raisy]];

    for (const [stack, base, colour] of pairs) {
      const band = metrics.map((m) => ({
        low: bandOf(base, base.reply_low)[m.key],
        mid: bandOf(base, base.reply_mid)[m.key],
        high: bandOf(base, base.reply_high)[m.key],
      }));
      // Every segment reports its own height but the metric's WHOLE band, so the tooltip reads the
      // same whichever part of the bar the reader lands on.
      const datum = (take: (x: { low: number; mid: number; high: number }) => number) =>
        band.map((x, i) => ({ value: take(x), base: base.label, metric: metrics[i].label, ...x }));

      out.push({
        name: base.label, type: 'bar', stack, barWidth: 30,
        itemStyle: { color: colour },
        data: datum((x) => x.low),
      });
      out.push({
        name: base.label, type: 'bar', stack, barWidth: 30,
        itemStyle: { color: tint(colour, 0.62), borderRadius: [3, 3, 0, 0] },
        data: datum((x) => x.high - x.low),
      });
    }
    return out;
  }

  const sankeyOption = {
    backgroundColor: surface,
    tooltip: {
      trigger: 'item',
      backgroundColor: isDark ? '#111827' : '#fff',
      borderColor: axis,
      textStyle: { color: text, fontSize: 12 },
      formatter: (p: any) => {
        if (p.dataType === 'edge') {
          const v = p.data.value as number;
          return `<b>${p.data.source}</b> → <b>${p.data.target}</b><br/>${fmt(v)} reachouts · ${pct(v / built.R)} of the input`;
        }
        return `<b>${p.name}</b><br/>${fmt(p.data.value as number)} · ${pct((p.data.value as number) / built.R)} of the input`;
      },
    },
    series: [
      {
        type: 'sankey',
        orient: narrow ? 'vertical' : 'horizontal',
        left: '2%', right: narrow ? '4%' : '16%', top: '3%', bottom: '3%',
        nodeWidth: 15, nodeGap: 14, draggable: false,
        emphasis: { focus: 'adjacency' },
        data: built.nodes.map((n) => ({ name: n.name, depth: n.depth, value: n.value, itemStyle: { color: n.color, borderWidth: 0 } })),
        links: built.links,
        label: { color: text, fontSize: narrow ? 10.5 : 11, fontWeight: 600, formatter: (p: any) => `${p.name}\n${fmt(p.value)}` },
        lineStyle: { color: 'source', opacity: 0.35, curveness: 0.5 },
      },
    ],
  };

  return (
    <div className="w-full">
      {/* ---- the 8 steps, in order, with the unmeasured ones marked ---- */}
      <ol className="mb-4 flex flex-wrap gap-x-2 gap-y-2 text-xs">
        {STEPS.map((s) => (
          <li
            key={s.key}
            className="flex items-center gap-1.5 rounded-full border px-2.5 py-1"
            style={{
              borderColor: s.measured ? (isDark ? '#334155' : '#cbd5e1') : PALETTE.gate,
              color: s.measured ? text : (isDark ? '#fbbf24' : '#b45309'),
              background: s.measured ? 'transparent' : (isDark ? 'rgba(245,158,11,.08)' : 'rgba(245,158,11,.07)'),
            }}
            title={s.note ?? ''}
          >
            <b>{s.n}</b>
            <span>{s.label}</span>
            {!s.measured && <span className="font-semibold">· unmeasured</span>}
          </li>
        ))}
      </ol>

      {/* ---- what Raisy adds: shown OUTSIDE the sankey, because a Sankey draws one run ---- */}
      <div className="mb-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm font-semibold" style={{ color: text }}>What the pre-filter is worth</div>
          <div className="text-xs" style={{ color: sub }}>
            The same {fmt(lane.reachouts)} reachouts a month, on the two reply bands blog16
            publishes. <b>Solid = the bottom of the published band</b> (at least this much);{' '}
            <b>lighter = the rest of the band above it</b> (how much higher it reaches). Hover any bar
            for the band and its midpoint.
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold" style={{ color: sub }}>
            With Raisy ÷ without, at each band's midpoint
          </div>
          <div className="text-xl font-bold" style={{ color: PALETTE.raisy }}>{uplift.toFixed(1)}×</div>
          <div className="text-xs" style={{ color: sub }}>
            {fmt(rMid.raised)} {metrics[2].label.toLowerCase()} a month against {fmt(wMid.raised)} —
            but the bands overlap, so the honest claim is a range:{' '}
            <b>{worstRatio.toFixed(1)}×</b> at the conservative ends to{' '}
            <b>{bestRatio.toFixed(1)}×</b> at the optimistic ones.
          </div>
        </div>
      </div>
      <ReactECharts option={valueOption} style={{ height: narrow ? 210 : 190, width: '100%' }} notMerge />

      {/* ---- the flow itself ---- */}
      <div className="mb-1 mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm font-semibold" style={{ color: text }}>What it takes, step by step</div>
          <div className="text-xs" style={{ color: sub }}>
            This is the run <b>with Raisy</b>, on the fit-ranked band. Even so,{' '}
            <b>{pct(1 - raisy.reply_mid)}</b> of the {fmt(lane.reachouts)} reachouts still get no reply
            — the pre-filter moves the reply rate, it does not make replies certain. Steps 1–3 are the
            pre-filter and have no measured volume, so they set no width; step 7 carries every meeting
            through, because the 35% win rate spans 6 to 8.
          </div>
        </div>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-semibold" style={{ color: sub }}>Lane</span>
          <select
            value={laneKey}
            onChange={(e) => setLaneKey(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ borderColor: axis, background: isDark ? '#111827' : '#fff', color: text }}
          >
            {LANES.map((l) => (
              <option key={l.key} value={l.key}>{l.label} — {l.reachouts}/mo</option>
            ))}
          </select>
        </label>
      </div>
      <ReactECharts option={sankeyOption} style={{ height: narrow ? 780 : height, width: '100%' }} notMerge />

      <p className="mt-2 text-xs" style={{ color: sub }}>
        {raisy.label}: {raisy.source}. {without.label}: {without.source}. Meeting rate 40% of responses and win rate 35% of meetings, per blog16 and FM-0013. Lane volume: {lane.reachouts}/month ({lane.source}). Steps 1–3 (the list, the geography filter and the fit threshold) and step 7's own share are unmeasured and set no band width.
      </p>
    </div>
  );
};
